import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';

import { portalForgotPassword } from '@/api/generated';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';

import { AuthLayout } from './AuthLayout';
import { forgotPasswordSchema, type ForgotPasswordValues } from './schemas';

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const [requestedEmail, setRequestedEmail] = useState('');

  const request = useMutation({
    mutationFn: async (values: ForgotPasswordValues) => {
      setRequestedEmail(values.email);
      // The staff endpoint, not the mobile app's. A portal address is not an
      // app address, so the app's reset never reached these accounts.
      await portalForgotPassword({ body: { email: values.email } });
    },
    // The same answer either way. Reporting a failure here would tell an
    // attacker which email addresses are registered.
    onSettled: () => setSent(true),
  });

  if (sent) {
    return (
      <AuthLayout title="Check your email" testId="forgot-password-sent">
        <Alert severity="success" data-testid="forgot-password-sent-message">
          If that email address has a portal account, we have sent it a code for resetting
          the password.
        </Alert>
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            component={RouterLink}
            to={ROUTES.resetPassword}
            state={{ email: requestedEmail }}
            data-testid="forgot-password-sent-enter-code-button"
          >
            Enter the code
          </Button>
          <Link
            component={RouterLink}
            to={ROUTES.signIn}
            variant="body2"
            data-testid="forgot-password-sent-sign-in-link"
            sx={{ textAlign: 'center' }}
          >
            Back to sign in
          </Link>
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgotten your password?"
      description="Enter your email address and we will send you a code."
      testId="forgot-password"
      footer={
        <Link
          component={RouterLink}
          to={ROUTES.signIn}
          variant="body2"
          data-testid="forgot-password-sign-in-link"
        >
          Back to sign in
        </Link>
      }
    >
      <Box
        component="form"
        onSubmit={(event) => void handleSubmit((values) => request.mutate(values))(event)}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        noValidate
        data-testid="forgot-password-form"
      >
        <Field
          label="Email address"
          error={errors.email?.message}
          testId="forgot-password-email"
        >
          <TextField type="email" autoComplete="username" {...register('email')} />
        </Field>

        <Button
          type="submit"
          loading={request.isPending}
          data-testid="forgot-password-submit-button"
        >
          Send the code
        </Button>
      </Box>
    </AuthLayout>
  );
}
